











































import Vue from 'vue';
import { IQuickNavEntry, filterEntries } from '@/util/QuickNavIndex';
import JobProvider from '@/api/JobProvider';
import SkillProvider from '@/api/SkillProvider';
import ItemsProvider, { CashFilter } from '@/api/ItemsProvider';
import SpriteIcon from '@/components/util/SpriteIcon.vue';
import ItemIcon from '@/old/game/ItemIcon.vue';

const STATIC_ENTRIES: IQuickNavEntry[] = [
    { label: "Home", keywords: "index welcome region", icon: "fa-home", kind: "page", route: "/" },
    { label: "Classes", keywords: "jobs class tree", icon: "fa-users", kind: "page", route: "/jobs" },
    { label: "Items", keywords: "item home", icon: "fa-cube", kind: "page", route: "/items" },
    { label: "Item Search", keywords: "items find", icon: "fa-search", kind: "page", route: { name: "item-search" } },
    { label: "Titles", keywords: "item titles", icon: "fa-user", kind: "page", route: { name: "titles" } },
    { label: "Monsters", keywords: "mobs bosses", icon: "fa-paw", kind: "page", route: "/monsters" },
    { label: "Skills", keywords: "skill search", icon: "fa-book", kind: "page", route: "/skills" },
    { label: "Skill Simulator", keywords: "skillsim build planner", icon: "fa-flask", kind: "page", route: { name: "skill-sim" } },
    { label: "Text", keywords: "uistring strings messages mid", icon: "fa-font", kind: "page", route: "/text/uistring" },
];

const LIVE_SEARCH_DEBOUNCE_MS = 1000;
const LIVE_SEARCH_MIN_CHARS = 3;
const LIVE_SEARCH_LIMIT = 5;

interface IData {
    query: string;
    selectedIndex: number;
    jobEntries: IQuickNavEntry[];
    jobsLoaded: boolean;
    liveSkills: IQuickNavEntry[];
    liveItems: IQuickNavEntry[];
    liveTimer: number | null;
}

export default Vue.extend({
    components: {
        SpriteIcon,
        ItemIcon,
    },
    props: {
        open: {
            type: Boolean as () => boolean,
            required: true,
        },
    },
    data(): IData {
        return {
            query: "",
            selectedIndex: 0,
            jobEntries: [],
            jobsLoaded: false,
            liveSkills: [],
            liveItems: [],
            liveTimer: null,
        };
    },
    computed: {
        results(): IQuickNavEntry[] {
            const matches = filterEntries(this.query, [...STATIC_ENTRIES, ...this.jobEntries], 8);
            const q = this.query.trim();
            if (!q) {
                return matches;
            }

            const ret: IQuickNavEntry[] = [...matches];

            if (/^\d+$/.test(q)) {
                ret.push({
                    label: `Go to monster #${q}`,
                    keywords: q,
                    icon: "fa-paw",
                    kind: "action",
                    route: `/monsters/${q}`,
                });
            }

            ret.push({
                label: `Search skills for "${q}"`,
                keywords: q,
                icon: "fa-book",
                kind: "action",
                route: { name: "skills", query: { q } },
            });
            ret.push(...this.liveSkills);

            ret.push({
                label: `Search items for "${q}"`,
                keywords: q,
                icon: "fa-cube",
                kind: "action",
                route: { name: "item-search", query: { q, name: q } },
            });
            ret.push(...this.liveItems);

            return ret;
        },
    },
    watch: {
        open(isOpen: boolean) {
            if (isOpen) {
                this.query = "";
                this.selectedIndex = 0;
                this.clearLiveResults();
                this.loadJobs();
                Vue.nextTick().then(() => {
                    const input = this.$refs.input as HTMLInputElement;
                    if (input) {
                        input.focus();
                    }
                });
            }
        },
        query() {
            this.selectedIndex = 0;
            this.clearLiveResults();

            const q = this.query.trim();
            if (q.length >= LIVE_SEARCH_MIN_CHARS) {
                this.liveTimer = window.setTimeout(() => this.runLiveSearch(q), LIVE_SEARCH_DEBOUNCE_MS);
            }
        },
    },
    beforeDestroy() {
        if (this.liveTimer != null) {
            window.clearTimeout(this.liveTimer);
        }
    },
    methods: {
        close() {
            this.$emit("close");
        },
        move(delta: number) {
            const n = this.results.length;
            if (n === 0) {
                return;
            }
            this.selectedIndex = (this.selectedIndex + delta + n) % n;
        },
        pick(index: number) {
            const target = this.results[index];
            if (!target) {
                return;
            }
            this.close();
            this.$router.push(target.route as any);
        },
        clearLiveResults() {
            if (this.liveTimer != null) {
                window.clearTimeout(this.liveTimer);
                this.liveTimer = null;
            }
            this.liveSkills = [];
            this.liveItems = [];
        },
        runLiveSearch(q: string) {
            this.liveTimer = null;

            SkillProvider.searchSkillsByName(q).then((skills) => {
                if (this.query.trim() !== q) {
                    return; // stale
                }
                this.liveSkills = skills
                    .filter((s) => s.name && s.name.message)
                    .slice(0, LIVE_SEARCH_LIMIT)
                    .map((s) => ({
                        label: s.name.message,
                        keywords: "",
                        icon: "fa-book",
                        kind: "skill" as const,
                        spriteIcon: s.skillIcon,
                        child: true,
                        route: `/skills/${s.id}`,
                    }));
            }, () => { /* leave empty on error */ });

            ItemsProvider.search({
                name: q,
                ranks: [],
                types: [],
                parts: [],
                categories: [],
                classes: [],
                cash: CashFilter.ALL,
                minLevel: 0,
                maxLevel: 100,
                //  pageSize 0 stays unpaged (server default), matching this
                //  call's pre-existing behaviour of pulling every match and
                //  slicing client-side below.
                page: 0,
                pageSize: 0,
                sort: 'relevance',
                dir: 'desc',
            }).then((res) => {
                if (this.query.trim() !== q) {
                    return; // stale
                }
                this.liveItems = res.items
                    .filter((it) => it.name && it.name.name)
                    .slice(0, LIVE_SEARCH_LIMIT)
                    .map((it) => ({
                        label: it.name.name as string,
                        keywords: "",
                        icon: "fa-cube",
                        kind: "item" as const,
                        itemIcon: { iconIndex: it.iconIndex as number, rank: it.rank as string },
                        child: true,
                        route: { name: "item-view", params: { itemId: String(it.id) } },
                    }));
            }, () => { /* leave empty on error */ });
        },
        loadJobs() {
            if (this.jobsLoaded) {
                return;
            }
            this.jobsLoaded = true;
            JobProvider.getAllJobs().then((jobs) => {
                this.jobEntries = jobs
                    .filter((j) => j.name && j.name.message)
                    .map((j) => ({
                        label: j.name.message,
                        keywords: `class job ${j.id}`,
                        icon: "fa-user-o",
                        kind: "class" as const,
                        spriteIcon: j.icon,
                        route: { name: "job-view", params: { jobId: String(j.id) } },
                    }));
            }, () => {
                this.jobsLoaded = false;
            });
        },
    },
});
